import { Component, type ErrorInfo, type ReactNode } from 'react';
import './error-boundary.css';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** 标题，默认「页面出错了」 */
  title?: string;
  /** 一句话说明，默认「该功能加载失败，可能是浏览器不支持或数据异常。」 */
  message?: string;
  /** 紧凑模式：用于局部降级（如星图区域），铺满父容器而非整屏 */
  compact?: boolean;
  /** 是否提供「返回全国态势」按钮（局部降级通常仍可返回） */
  showHome?: boolean;
  /**
   * 变更时自动重置错误态（用于路由 / 选中项变化后允许重试渲染）。
   * 传入字符串等可比较的值即可。
   */
  resetKey?: unknown;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * 通用错误边界（P6.60）。
 *
 * 背景：此前 main.tsx 直接把 <App /> 交给 createRoot，没有 ErrorBoundary，
 * 任一渲染期异常（如星图页 WebGL 创建失败）都会让 React 卸载整棵树 → 整页白屏，
 * 用户看不到任何提示。本组件为整页与「局部区域（星图）」提供兜底。
 *
 * 用法：
 *   <ErrorBoundary>...</ErrorBoundary>                         // 整页兜底
 *   <ErrorBoundary compact title="3D 星图不可用" message="..." /> // 局部降级
 */
export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // 完整错误栈，便于排查（不吞异常）
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] 捕获到渲染异常：', error, error.stack, info.componentStack);
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleHome = (): void => {
    // 用整页跳转而非路由跳转：即使错误发生在路由内部也能可靠返回
    window.location.assign('/map');
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const {
      title = '页面出错了',
      message = '该功能加载失败，可能是浏览器不支持或数据异常。',
      compact = false,
      showHome = true,
    } = this.props;

    return (
      <div className={`eb ${compact ? 'eb--compact' : ''}`} role="alert">
        <div className="eb__box">
          <div className="eb__icon" aria-hidden="true">
            ⚠
          </div>
          <h2 className="eb__title">{title}</h2>
          <p className="eb__msg">{message}</p>
          <p className="eb__detail">{error.message}</p>
          <div className="eb__actions">
            <button type="button" className="btn btn--primary" onClick={this.handleReload}>
              重新加载
            </button>
            {showHome && (
              <button type="button" className="btn btn--secondary" onClick={this.handleHome}>
                返回全国态势
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}
